// apps/client/src/world/objects/tree.ts — instanced trees; a fruit tree adds a second instanced proxy of six fruits on its canopy
// (on its surface, mostly on the camera's side: M1 playtest).
// M1-T15: fruit is personal (hidden for you for 3 days after you shake it), planted saplings grow through three stages drawn inline
// (no code:sapling builder, §7.4), and a shake wobbles the tree.
import { getEntry, type FruitType } from '@whiskerton/content';
import { hash32, type ObjectId, type TreeState, type WorldObject } from '@whiskerton/shared';
import { canShake, treeStage } from '@whiskerton/shared/rules/fruit';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { toyMaterial } from '../../characters/doll';
import { patchObject } from '../../engine/shader/worldPatch';
import { farModel, inFarModel, InstanceProxy, modelTemplate, type ObjectView, type ViewCtx } from '../objectView';

export const FRUIT_COLOUR: Readonly<Record<FruitType, string>> = { apple: '#E0503A', orange: '#F2A03C', pear: '#C9D46A', peach: '#F6A57E', cherry: '#C8324A' };
/** The canopy as an ellipsoid; `spots` (trees and rocks, 2026-10-16) are the fruit spots a code-built tree marked, in model space. */
interface Canopy { y: number; rx: number; ry: number; rz: number; spots?: THREE.Vector3[] }

/** The canopy as an ellipsoid fitted to the model's upper part (kit trees are a trunk under one or more leaf balls): the box of every
 *  vertex above CANOPY_FROM of the model's height, so a wide root spread or trunk never stretches it. */
const CANOPY_FROM = 0.4;
function canopyOf(model: THREE.Object3D): Canopy {
  model.updateMatrixWorld(true);
  let spots: THREE.Vector3[] | undefined;
  model.traverse((c) => {                                         // a code-built tree's own fruit spots, in the model's space
    const at = c.userData.fruitSpots as [number, number, number][] | undefined;
    if (at && !spots) spots = at.map(([x, y, z]) => new THREE.Vector3(x, y, z).applyMatrix4(c.matrixWorld));
  });
  const all = new THREE.Box3(), v = new THREE.Vector3(), meshes: THREE.Mesh[] = [];
  model.traverse((c) => { if ((c as THREE.Mesh).isMesh && !c.userData.blob && !inFarModel(c)) { meshes.push(c as THREE.Mesh); all.expandByObject(c); } });
  if (all.isEmpty()) return { y: 1.2, rx: 0.5, ry: 0.5, rz: 0.5, spots };
  const from = all.min.y + (all.max.y - all.min.y) * CANOPY_FROM, top = new THREE.Box3();
  for (const m of meshes) {
    const pos = m.geometry.getAttribute('position');
    for (let i = 0; i < pos.count; i++) { v.fromBufferAttribute(pos, i).applyMatrix4(m.matrixWorld); if (v.y >= from) top.expandByPoint(v); }
  }
  const box = top.isEmpty() ? all : top, size = box.getSize(new THREE.Vector3()), c = box.getCenter(new THREE.Vector3());
  return { y: c.y, rx: size.x / 2, ry: size.y / 2, rz: size.z / 2, spots };
}
/** Fruit radius (M1 playtest: 0.09 balls were specks on a 4-unit tree). */
const FRUIT_R = 0.15;
/** Where the six fruits hang: [angle off the camera side (+z) in radians, height on the canopy (−1 bottom … 1 top)], mostly on the
 *  south half the 3/4 camera looks at, a couple round the sides. */
const FRUIT_AT: readonly (readonly [number, number])[] = [[-1.9, 0.1], [-1.05, -0.35], [-0.3, 0.2], [0.3, -0.3], [1.05, 0.25], [1.9, -0.15]];
/** Keyed by the tree's model template (one per asset service and catalog id, `modelTemplate`), so a canopy is never measured from
 *  another service's model. */
const fruitTemplates = new WeakMap<THREE.Object3D, Map<FruitType, THREE.Mesh>>();
/** Six fruits on the canopy surface, merged into one geometry, so every fruit tree of a kind and fruit is one instanced draw. The
 *  fruit sits on the builder's spots when the tree has them, else on the ellipsoid's surface, half out of the leaves. Its far model
 *  (a hidden child, trees and rocks 2026-10-16) is six coarse balls, drawn with the tree's own far model. */
function fruitTemplate(model: THREE.Object3D, fruit: FruitType, c: Canopy): THREE.Mesh {
  let byFruit = fruitTemplates.get(model);
  if (!byFruit) fruitTemplates.set(model, (byFruit = new Map()));
  let t = byFruit.get(fruit);
  if (t) return t;
  const at = c.spots ?? FRUIT_AT.map(([a, up]) => {
    const k = Math.sqrt(1 - up * up);
    return new THREE.Vector3(Math.sin(a) * c.rx * k, c.y + up * c.ry, Math.cos(a) * c.rz * k);
  });
  const balls = (make: () => THREE.BufferGeometry) => mergeGeometries(at.map((p) => make().translate(p.x, p.y, p.z)), false)!;
  t = new THREE.Mesh(balls(() => new THREE.SphereGeometry(FRUIT_R, 10, 8)), toyMaterial(FRUIT_COLOUR[fruit]));
  t.add(farModel(new THREE.Mesh(balls(() => new THREE.IcosahedronGeometry(FRUIT_R * 1.1, 0)), t.material)));
  t.userData.wind = model.userData.wind;                          // in the tree's own space, so it sways with the leaves it sits on
  byFruit.set(fruit, t);
  return t;
}
const fruitKey = (fruit: FruitType, obj: WorldObject) => `fruit:${fruit}:${obj.catalogId}`;
function showFruit(obj: WorldObject, view: THREE.Object3D, visible: boolean): void {
  const fruit = (obj.state as TreeState).fruit ?? null;
  for (const c of view.children) if (c instanceof InstanceProxy && c.key.startsWith('fruit:')) c.visible = false;
  if (!fruit || !visible) return;
  const tree = view.children.find((c): c is InstanceProxy => c instanceof InstanceProxy && c.key === obj.catalogId);
  if (!tree) return;                                              // a sapling: no canopy to hang fruit on
  const key = fruitKey(fruit, obj);
  let p = view.children.find((c): c is InstanceProxy => c instanceof InstanceProxy && c.key === key);
  if (!p) {                                                       // fruit appeared after the build: the outdoor scene files the new proxy on touch
    p = new InstanceProxy(key, fruitTemplate(tree.template, fruit, view.userData.canopy as Canopy));
    view.add(p);
  }
  p.visible = true;
}

/** What you see of a tree: its growth stage, and whether its fruit shows for you (mature, has fruit, not shaken by you in 3 days). */
export function treeVisual(state: TreeState, lastShakenAt: number | null, now: number): { stage: ReturnType<typeof treeStage>; fruitVisible: boolean } {
  const stage = treeStage(state, now);
  return { stage, fruitVisible: stage === 'mature' && canShake(state, lastShakenAt, now) };
}

/** Internal (beyond the M1-T15 contract; only game/systems/fishing.ts calls it): the synced town clock for growth and fruit
 *  cooldowns (ViewCtx.now is the time of the outdoor build). */
let clock: (() => number) | null = null;
export function setTreeClock(fn: (() => number) | null): void { clock = fn; }

// ---------- shake wobble: ±0.08 rad for 0.6 s. The contract says "canopy wobble", but trees are instanced, so the whole tree rocks
// on its base (a proxy has no separate canopy), and the view is re-filed every frame while it wobbles. `tickTreeShakes` is internal
// (beyond the M1-T15 contract; only game/systems/fishing.ts calls it).
const SHAKE_MS = 600, SHAKE_RAD = 0.08;
const shakes = new Map<ObjectId, number>();
const wallClock = () => performance.now();
export function shakeTree(id: ObjectId): void { shakes.set(id, wallClock()); }
function wobbleOf(id: ObjectId): number {
  const start = shakes.get(id);
  if (start === undefined) return 0;
  const t = (wallClock() - start) / SHAKE_MS;
  return t < 1 ? Math.sin(t * Math.PI * 6) * SHAKE_RAD * (1 - t) : 0;
}
/** Calls `refresh(id)` (OutdoorScene.updateObject) for every wobbling tree; a finished wobble gets one last refresh, at rest. */
export function tickTreeShakes(refresh: (id: ObjectId) => void): void {
  for (const [id, start] of [...shakes]) {
    const done = wallClock() - start >= SHAKE_MS;
    refresh(id);
    if (done) shakes.delete(id);
  }
}

// ---------- saplings, drawn inline: stage 1 a sprout on a mound of dug soil, stage 2 a 0.5-tall stem with a leaf ball, stage 3 a
// 1.2-tall young tree. The sprout stands up off the grass in a lighter green: a flat leaf on the lawn was nearly invisible (M1 playtest).
const leafMat = new THREE.MeshStandardMaterial({ color: '#6CBE6B', roughness: 0.8, metalness: 0 });
const sproutMat = new THREE.MeshStandardMaterial({ color: '#9BD86A', roughness: 0.8, metalness: 0 });
const trunkMat = new THREE.MeshStandardMaterial({ color: '#8A6A4F', roughness: 0.8, metalness: 0 });
const soilMat = new THREE.MeshStandardMaterial({ color: '#7A5638', roughness: 1, metalness: 0 });
type SaplingStage = 'sapling1' | 'sapling2' | 'sapling3';
/** Internal (exported for tests). */
export function sapling(stage: SaplingStage): THREE.Group {
  const g = new THREE.Group(); g.name = stage;
  if (stage === 'sapling1') {
    const soil = new THREE.Mesh(new THREE.SphereGeometry(0.22, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 0.32, 1), soilMat);
    soil.name = 'soil'; soil.receiveShadow = true; g.add(soil);
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.026, 0.24, 6), sproutMat); stem.position.y = 0.12 + 0.04;
    stem.castShadow = true; g.add(stem);
    for (const s of [-1, 1]) {
      const l = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 6).scale(1.3, 0.35, 0.75), sproutMat);
      l.position.set(s * 0.1, 0.3, 0); l.rotation.z = s * 0.45; l.castShadow = true; g.add(l);
    }
  } else {
    const h = stage === 'sapling2' ? 0.5 : 1.2;
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.04, h, 7), trunkMat); stem.position.y = h / 2;
    const ball = new THREE.Mesh(new THREE.IcosahedronGeometry(stage === 'sapling2' ? 0.18 : 0.42, 1), leafMat); ball.position.y = h + 0.1; ball.name = 'canopy';
    stem.castShadow = true; ball.castShadow = true;
    g.add(stem, ball);
  }
  patchObject(g);                                                 // added after the view was filed, so it curves with the world itself
  return g;
}

/** Brings a view in line with the tree's growth stage, your fruit visibility and its wobble. */
function render(obj: WorldObject, view: THREE.Object3D, ctx: ViewCtx): void {
  const vis = treeVisual(obj.state as TreeState, ctx.you?.harvests[obj.id] ?? null, clock?.() ?? ctx.now);
  if (view.userData.stage !== vis.stage) {
    view.userData.stage = vis.stage;
    for (const c of [...view.children]) if (!(c instanceof InstanceProxy)) view.remove(c);
    let tree = view.children.find((c): c is InstanceProxy => c instanceof InstanceProxy && c.key === obj.catalogId);
    if (vis.stage === 'mature') {
      if (!tree) { tree = new InstanceProxy(obj.catalogId, view.userData.template as THREE.Object3D); view.add(tree); }
      tree.visible = true;
    } else {
      if (tree) tree.visible = false;                             // a proxy stays filed with its batch; hidden, it draws nothing
      view.add(sapling(vis.stage));
    }
  }
  showFruit(obj, view, vis.fruitVisible);
  view.rotation.z = wobbleOf(obj.id);
}

export const view: ObjectView = {
  kinds: ['tree'],
  perResident: true,
  async build(obj, ctx) {
    const model = await modelTemplate(ctx.assets, obj.catalogId);   // saplings load it too, so growing up swaps in synchronously
    const g = new THREE.Group();
    g.userData.canopy = canopyOf(model);
    g.userData.template = model;
    const e = getEntry(obj.catalogId);
    if (e && e.kind === 'world' && e.footprint[0] === 1 && e.footprint[1] === 1) g.scale.setScalar(1 + ((hash32(obj.id) % 1601) / 800 - 1) * 0.08);   // ±8 %
    render(obj, g, ctx);
    return g;
  },
  update(obj, v, ctx) { render(obj, v, ctx); },
};
